import { format } from 'date-fns';
import { supabase } from './supabase';
import { updateCaregiver } from './axiscare';
import { getRole, getOfferLetterHtml, TEMPLATE_CODE_TO_POSITION } from './onboardingTemplates';
import { notifyDocumentRequestReady, notifyOfferLetterReady } from './notifications';
import { parseLocalDate } from './dateUtils';

// Changing an offer letter and sending it again (2026-10-02).
//
// Two cases, one path:
//   - The letter is still out for signature: its text is replaced in place.
//     The signing link stays the same — for a candidate it is the whole offer
//     bundle's link — so the email they already have now opens the new letter.
//   - The letter has been signed: the signed copy is kept untouched as the
//     record of what was first agreed, and a new "Offer Letter (revised …)" is
//     sent on its own link. Only for people already active: signing it then
//     records the signature without re-running activation (the alreadyActive
//     branch in submit-staff-signatures), so no second AxisCare account and no
//     second welcome email.
//
// The pay rate, role, track and start date are written to the staff record
// straight away (decided 2026-10-02) — payroll reads the rate from there when
// a run is pulled, so it applies from the next run, signed or not. The change
// and the reason are logged as an `offer_revised` onboarding event, which the
// office notice in the General Notifier Space is built from.

export const TRACK_LABEL = { employee: 'W2', contractor: '1099' };
export const payTypeFor = (roleCode) => (getRole(roleCode)?.payUnit === 'weekly' ? 'weekly' : 'hourly');
export const fmtPay = (rate, payType) =>
  rate == null || rate === '' ? 'not set' : `$${Number(rate).toFixed(2)}${payType === 'weekly' ? '/wk' : '/hr'}`;
const fmtDay = (d) => (d ? format(parseLocalDate(String(d).slice(0, 10)), 'MMM d, yyyy') : 'not set');

/** What changed on the record, in words, for the event log and the notice. */
export function describeChanges(staff, next) {
  const out = [];
  const oldType = staff.pay_type || 'hourly';
  if (Number(staff.pay_rate) !== Number(next.payRate) || oldType !== next.payType) {
    out.push(`pay ${fmtPay(staff.pay_rate, oldType)} → ${fmtPay(next.payRate, next.payType)}`);
  }
  if ((staff.hire_role_code || '') !== next.roleCode) {
    out.push(`role ${getRole(staff.hire_role_code)?.label || staff.hire_role_code || 'not set'} → ${getRole(next.roleCode)?.label || next.roleCode}`);
  }
  if ((staff.employment_type || '') !== next.employmentType) {
    out.push(`${TRACK_LABEL[staff.employment_type] || staff.employment_type || 'not set'} → ${TRACK_LABEL[next.employmentType]}`);
  }
  if ((staff.start_date || '').slice(0, 10) !== (next.startDate || '')) {
    out.push(`start date ${fmtDay(staff.start_date)} → ${fmtDay(next.startDate)}`);
  }
  return out;
}

/** Who may have a signed offer revised: an active, non-agency staff member. */
export const canReviseSigned = (staff) =>
  staff?.onboarding_status === 'active' && staff.employment_type !== 'agency' && staff.hire_track !== 'agency';

/**
 * Build the letter, save the record, send it. Returns { mode: 'edited' |
 * 'revised', changes, axisNote }. Throws with a message for the person when
 * nothing could be sent; the staff record is put back if the letter fails.
 */
export async function reviseOfferLetter(staff, { roleCode, employmentType, payRate, startDate, signBy, reason, officeName, officeRole }) {
  if (!staff.email) throw new Error(`${staff.full_name} has no email on file — add one before sending.`);
  if (!['employee', 'contractor'].includes(employmentType)) throw new Error('Choose W2 or 1099.');
  const rate = Number(payRate);
  if (!Number.isFinite(rate) || rate <= 0) throw new Error('Enter the pay rate.');
  if (!reason?.trim()) throw new Error('Say why the offer is being revised — it is recorded with the letter.');

  const lang = staff.preferred_language === 'es' ? 'es' : 'en';
  const payType = payTypeFor(roleCode);
  const content = getOfferLetterHtml(roleCode, employmentType, { full_name: staff.full_name, pay_rate: rate }, {
    startDateStr: startDate ? format(parseLocalDate(startDate), 'MMMM d, yyyy') : 'a mutually agreed date',
    acceptByStr: signBy ? format(parseLocalDate(signBy), 'MMMM d, yyyy') : '',
  }, lang);
  if (!content) throw new Error(`There is no ${TRACK_LABEL[employmentType]} offer letter for this role.`);

  const { data: pending, error: pendErr } = await supabase.from('staff_documents')
    .select('id, sign_token, doc_label').eq('staff_id', staff.id).eq('doc_type', 'offer_letter').eq('status', 'sent').maybeSingle();
  if (pendErr) throw pendErr;
  if (!pending && !canReviseSigned(staff)) {
    throw new Error('A signed offer can only be revised for someone who is already active.');
  }

  const next = { roleCode, employmentType, payRate: rate, payType, startDate: startDate || '' };
  const changes = describeChanges(staff, next);

  // 1. The record — what payroll, Personnel and AxisCare read.
  const before = {
    pay_rate: staff.pay_rate, pay_type: staff.pay_type, start_date: staff.start_date,
    hire_role_code: staff.hire_role_code, employment_type: staff.employment_type, hire_track: staff.hire_track,
    offer_expires_at: staff.offer_expires_at,
  };
  const { error: staffErr } = await supabase.from('staff').update({
    pay_rate: rate, pay_type: payType, start_date: startDate || null,
    hire_role_code: roleCode, employment_type: employmentType, hire_track: employmentType,
    ...(TEMPLATE_CODE_TO_POSITION[roleCode] || {}),
    ...(staff.onboarding_status === 'offer_sent' && signBy ? { offer_expires_at: new Date(`${signBy}T23:59:59`).toISOString() } : {}),
  }).eq('id', staff.id);
  if (staffErr) throw new Error(`Could not save the new terms: ${staffErr.message}`);

  // 2. The letter.
  const now = new Date().toISOString();
  const countersign = { office_signed_by: officeName || null, office_signed_at: officeName ? now : null, office_signer_role: officeRole || null };
  let token; let label; let mode;
  if (pending) {
    mode = 'edited';
    token = pending.sign_token;
    label = pending.doc_label || 'Offer Letter';
    const { error } = await supabase.from('staff_documents')
      .update({ content, sent_at: now, reminder_sent_at: null, updated_at: now, ...countersign }).eq('id', pending.id);
    if (error) { await supabase.from('staff').update(before).eq('id', staff.id); throw new Error(`Could not update the letter: ${error.message}`); }
  } else {
    mode = 'revised';
    token = crypto.randomUUID();
    label = `Offer Letter (revised ${format(new Date(), 'MMM d, yyyy')})`;
    const { error } = await supabase.from('staff_documents').insert([{
      staff_id: staff.id, doc_type: 'offer_letter', doc_label: label, content,
      sign_token: token, status: 'sent', sent_at: now, ...countersign,
    }]);
    if (error) {
      await supabase.from('staff').update(before).eq('id', staff.id);
      throw new Error(error.code === '23505'
        ? 'A revised offer is already out for signature — refresh the page.'
        : `Could not create the revised letter: ${error.message}`);
    }
  }

  // 3. The email. A candidate still signing the bundle gets the offer email
  // they already know; someone already working gets the one-document request.
  const link = `${window.location.origin}/hr-sign/${token}`;
  if (staff.onboarding_status === 'offer_sent') await notifyOfferLetterReady(staff.email, staff.full_name, link);
  else await notifyDocumentRequestReady(staff.email, staff.full_name, label, link, lang);

  // 4. AxisCare holds the pay rate it was created with. Best-effort: CAERA's
  // payroll reads the staff record, not AxisCare.
  let axisNote = '';
  if (staff.axiscare_id && Number(staff.pay_rate) !== rate) {
    const { data: res, error } = await updateCaregiver(staff.axiscare_id, { payRate: rate });
    if (error || res?.errors?.length) axisNote = 'AxisCare still shows the old pay rate — update it there.';
  }

  // 5. The record of it — and, through a trigger, the office notice.
  await supabase.from('onboarding_events').insert({
    staff_id: staff.id, event_type: 'offer_revised',
    event_detail: [
      mode === 'edited' ? 'Unsigned offer letter edited and re-sent' : `${label} sent for signature`,
      changes.length ? changes.join('; ') : 'terms unchanged',
      `reason: ${reason.trim()}`,
    ].join(' — '),
    previous_status: staff.onboarding_status || null, new_status: staff.onboarding_status || null,
    actor: officeName || 'office',
  });

  return { mode, changes, axisNote };
}
